import { useFormikContext } from 'formik';
import React, { useRef, useState } from 'react';
import {
  Keyboard,
  ReturnKeyTypeOptions,
  TextInput,
  TextInputProps
} from 'react-native';
import styled from 'styled-components/native';

import Colors from '../../constants/Colors';
import { useFormContext } from './Form';
import { H5 } from '../Typography';

export type FormInputVariant = 'outlined' | 'survey' | 'inline';

const FormInputView = styled.View`
  width: 100%;
`;

const StyledTextInputView = styled.View<{
  hasHelperText: boolean;
  error?: boolean;
}>`
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  margin-bottom: ${p => (p.error || p.hasHelperText ? 4 : 16)}px;
  flex-direction: row;
  align-items: center;
  background-color: #fff;
  padding: 0 12px 0 12px;
`;

const StyledTextInput = styled.TextInput<{
  minHeight?: number;
  variant?: FormInputVariant;
  empty?: boolean;
  hasUnit?: boolean;
}>`
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  ${p => (p.variant === 'survey' ? `font-size: 15px;` : `font-size: 18px;`)}
  min-height: ${p => (p.minHeight ? `${p.minHeight}px` : 'auto')};
  border-radius: 8px;
  padding: 20px ${p => (p.hasUnit ? '6px' : '0')} 20px 12px;
  line-height: 23px;
  flex: 1;
  text-align: right;
` as React.FC<
  TextInputProps & {
    minHeight?: number;
    variant?: FormInputVariant;
    ref: any;
    empty?: boolean;
    hasUnit?: boolean;
  }
>;

const HelperText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 14px;
  line-height: 18px;
  color: ${Colors.formLabel};
  margin: 4px 4px 12px 4px;
`;

const ErrorText = styled(HelperText)<{ errorTextColor: string }>`
  color: ${p =>
    p.errorTextColor === 'dark' ? Colors.error : Colors.errorCaption};
`;

export type FormMeasurementProps = TextInputProps & {
  name: string;
  label?: string;
  helperText?: string;
  returnKeyType?: ReturnKeyTypeOptions;
  variant?: FormInputVariant;
  dismissKeyboard?: boolean;
  errorTextColor?: 'dark' | 'light';
  unit?: string;
  precision: number;
  scale: number;
};

const FormMeasurement: React.FC<FormMeasurementProps> = ({
  name,
  label,
  helperText,
  returnKeyType,
  variant = 'outlined',
  dismissKeyboard,
  errorTextColor = 'light',
  unit,
  precision,
  scale,
  ...props
}) => {
  const textInputRef = useRef<TextInput>(null!);
  const { handleChange, handleBlur, touched, errors, values, setFieldValue } =
    useFormikContext<any>();
  const { setFocusedInput, addInput, focusNext } = useFormContext();
  const [height, setHeight] = useState<number>();
  const error = touched[name] && errors[name];

  return (
    <FormInputView>
      <StyledTextInputView
        hasHelperText={Boolean(helperText)}
        error={Boolean(error)}
      >
        <H5 color='#615E6D'>{label}</H5>
        <StyledTextInput
          inputMode='decimal'
          hasUnit={Boolean(unit)}
          blurOnSubmit={false}
          placeholderTextColor={error ? Colors.error : Colors.placeholderColor}
          ref={(textInput: TextInput) => {
            textInputRef.current = textInput;
            addInput(textInput);
          }}
          {...props}
          minHeight={height}
          variant={variant}
          value={values[name]}
          empty={!values[name]}
          returnKeyType={returnKeyType}
          onChangeText={text => {
            const value = text.replace(/ /g, '');
            if (
              (value.length > 0 && isNaN(Number(value))) ||
              (value.includes('.') &&
                value.replace('.', '').length > precision) ||
              (!value.includes('.') && value.length > precision - scale) ||
              (!isNaN(Number(value)) &&
                +value > Number.MAX_VALUE &&
                +value < Number.MIN_VALUE)
            ) {
              setFieldValue(name, values[name]);
            } else {
              setFieldValue(name, value);
            }
            handleChange(name);
          }}
          onFocus={() => {
            setFocusedInput(textInputRef.current);
          }}
          onBlur={event => {
            handleBlur(name)(event);
            setFocusedInput(undefined);
          }}
          onContentSizeChange={event => {
            setHeight(event.nativeEvent.contentSize.height);
          }}
          onSubmitEditing={() => {
            if (dismissKeyboard) {
              Keyboard.dismiss();
            } else {
              focusNext();
            }
          }}
        />
        {unit && Boolean(values[name]) && <H5 color='dark'>{unit}</H5>}
      </StyledTextInputView>
      {error ? (
        <ErrorText errorTextColor={errorTextColor}>{error}</ErrorText>
      ) : (
        helperText && <HelperText>{helperText}</HelperText>
      )}
    </FormInputView>
  );
};

export default FormMeasurement;
